import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';

// Composant pour afficher les propositions en attente
const ProposalsNotification = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const [showProposals, setShowProposals] = useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadPendingCount();
  }, []);

  const loadPendingCount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const url = new URL('/api/admin/familles/propositions', API_BASE);
      url.searchParams.set('status', 'pending');
      
      const response = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingCount(data.length);
      }
    } catch (error) {
      console.error('Erreur chargement propositions:', error);
    }
  };

  if (pendingCount === 0) return null;

  return (
    <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-yellow-600" />
          <span className="font-semibold text-yellow-800">
            {pendingCount} proposition{pendingCount > 1 ? 's' : ''} en attente de validation
          </span>
        </div>
        <button
          onClick={() => window.location.href = '/admin/familles?tab=proposals'}
          className="px-4 py-2 text-white rounded hover:opacity-90"
          style={{ backgroundColor: 'rgb(196, 150, 96)' }}
        >
          Voir les propositions
        </button>
      </div>
    </div>
  );
};

export default function FamillesBlasonsManagement() {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const [loading, setLoading] = useState(false);
  const [familles, setFamilles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('');
  const [activeTab, setActiveTab] = useState('familles'); // 'familles' ou 'propositions'
  const [form, setForm] = useState({
    id_letter: '',
    nom_famille: '',
    description_blason: '',
    autre_info: '',
    blason_image_path: ''
  });

  const loadFamilles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };
      const url = new URL('/api/admin/familles-blasons', API_BASE);
      const res = await fetch(url.toString(), { headers });
      if (!res.ok) throw new Error('Erreur chargement');
      const data = await res.json();
      setFamilles(data.familles || []);
    } catch (e) {
      console.error('Erreur:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFamilles();
  }, []);

  const onCreate = () => {
    setForm({ id_letter: '', nom_famille: '', description_blason: '', autre_info: '', blason_image_path: '' });
    setEditingId(null);
    setShowForm(true);
  };

  const onEdit = (famille) => {
    setForm({
      id_letter: famille.id_letter || '',
      nom_famille: famille.nom_famille || '',
      description_blason: famille.description_blason || '',
      autre_info: famille.autre_info || '',
      blason_image_path: famille.blason_image_path || ''
    });
    setEditingId(famille.id);
    setShowForm(true);
  };

  const onDelete = async (id, nom) => {
    if (!window.confirm(`Supprimer "${nom}" ?`)) return;
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };
      const url = new URL(`/api/admin/familles-blasons/${id}`, API_BASE);
      const res = await fetch(url.toString(), { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Erreur suppression');
      await loadFamilles();
    } catch (e) {
      alert(e.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
      
      const method = editingId ? 'PUT' : 'POST';
      const endpoint = editingId ? `/api/admin/familles-blasons/${editingId}` : '/api/admin/familles-blasons';
      const url = new URL(endpoint, API_BASE);
      
      const res = await fetch(url.toString(), {
        method,
        headers,
        body: JSON.stringify(form)
      });
      
      if (!res.ok) throw new Error('Erreur sauvegarde');
      
      setShowForm(false);
      await loadFamilles();
    } catch (e) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadBlason = async (file) => {
    try {
      const token = localStorage.getItem('authToken');
      const fd = new FormData();
      fd.append('file', file);
      const url = new URL('/api/admin/upload-image', API_BASE);
      const res = await fetch(url.toString(), { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Upload échoué: ${errorText}`);
      }
      const data = await res.json();
      
      // L'API retourne une URL GridFS comme "/api/files/64abc123..."
      // On extrait le file_id depuis l'URL
      const fileUrl = data.url || data.file_url;
      let fileId = fileUrl;
      
      // Si c'est une URL complète, extraire le file_id
      if (fileUrl.includes('/api/files/')) {
        fileId = fileUrl.split('/api/files/').pop();
      }
      
      console.log('[UPLOAD SUCCESS]', { fileUrl, fileId });
      
      // Stocker le file_id dans blason_image_path
      setForm({...form, blason_image_path: fileId, blason_image_url_full: fileUrl});
      alert('✅ Image uploadée avec succès!');
    } catch (e) {
      console.error('[UPLOAD ERROR]', e);
      alert(`❌ Erreur upload: ${e.message}`);
    }
  };

  // Filtrage
  const filtered = familles.filter(f => {
    const matchSearch = !search || f.nom_famille.toLowerCase().includes(search.toLowerCase());
    const matchLetter = !selectedLetter || f.id_letter === selectedLetter;
    return matchSearch && matchLetter;
  });

  const letters = '7ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  if (showForm) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{editingId ? 'Modifier la famille' : 'Nouvelle famille'}</h2>
          <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Lettre (7, A-Z) *</label>
              <select
                required
                value={form.id_letter}
                onChange={(e) => setForm({...form, id_letter: e.target.value})}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">-- Sélectionner --</option>
                {letters.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nom de famille *</label>
              <input
                type="text"
                required
                value={form.nom_famille}
                onChange={(e) => setForm({...form, nom_famille: e.target.value})}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description du blason</label>
            <textarea
              rows="3"
              value={form.description_blason}
              onChange={(e) => setForm({...form, description_blason: e.target.value})}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Autres informations</label>
            <textarea
              rows="4"
              value={form.autre_info}
              onChange={(e) => setForm({...form, autre_info: e.target.value})}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Image du blason</label>
            {form.blason_image_path && (
              <div className="mb-3 p-3 border rounded bg-gray-50">
                <img 
                  src={form.blason_image_url_full || `${API_BASE}/api/files/${form.blason_image_path}`} 
                  alt="Blason" 
                  className="w-32 h-32 object-contain border"
                />
                <p className="text-sm text-gray-600 mt-2">{form.blason_image_path}</p>
                <button
                  type="button"
                  onClick={() => setForm({...form, blason_image_path: ''})}
                  className="mt-2 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Supprimer l'image
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => e.target.files[0] && uploadBlason(e.target.files[0])}
              className="block w-full text-sm text-gray-500"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="text-white px-6 py-2 rounded"
              style={{ backgroundColor: 'rgb(196, 150, 96)' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(176, 130, 76)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(196, 150, 96)'}
            >
              {editingId ? 'Mettre à jour' : 'Créer'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      {/* Notification propositions */}
      <ProposalsNotification />
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
          Familles & Blasons
        </h1>
        <button
          onClick={onCreate}
          className="text-white px-4 py-2 rounded"
          style={{ backgroundColor: 'rgb(196, 150, 96)' }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(176, 130, 76)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(196, 150, 96)'}
        >
          + Nouvelle famille
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="flex flex-wrap gap-2 items-center mb-4">
          <input
            type="text"
            placeholder="Rechercher par nom..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded px-3 py-2 flex-1 min-w-[200px]"
          />
          <select
            value={selectedLetter}
            onChange={(e) => setSelectedLetter(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="">Toutes les lettres</option>
            {letters.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <p className="text-sm text-gray-600">{filtered.length} famille(s)</p>
      </div>

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Lettre</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Nom</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Blason</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => (
                <tr key={f.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold">{f.id_letter}</td>
                  <td className="px-4 py-3 text-sm font-medium">{f.nom_famille}</td>
                  <td className="px-4 py-3">
                    {f.blason_image_path ? (
                      <img
                        src={f.blason_image_url_full || `${API_BASE}/api/files/${f.blason_image_path}`}
                        alt={f.nom_famille}
                        className="w-12 h-12 object-contain"
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">Pas d'image</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-md truncate">
                    {f.description_blason || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => onEdit(f)}
                        className="px-3 py-1 border rounded text-sm"
                        style={{ color: 'rgb(196, 150, 96)', borderColor: 'rgb(196, 150, 96)' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(246, 240, 232)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => onDelete(f.id, f.nom_famille)}
                        className="px-3 py-1 text-red-600 border border-red-600 rounded text-sm hover:bg-red-50"
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
